/**
 * Module catalogue and per-tenant switches.
 *
 * effective(tenant, module) = TenantModule override (set by the Super Admin) if present,
 *                              else the plan's `features[module]`,
 *                              else the module's default.
 * Core modules can't be switched off. Everything the guards, menus and permission
 * filtering need comes from `effectiveModules()`.
 */
export interface ModuleDef {
  key: string;
  name: string;
  nameBn: string;
  core?: boolean;          // always on
  defaultOn?: boolean;     // when neither plan nor override says anything
  requires?: string[];     // other modules that must also be on
}

export const MODULES: ModuleDef[] = [
  { key: "employees", name: "Employees", nameBn: "কর্মী", core: true },
  { key: "attendance", name: "Attendance", nameBn: "হাজিরা", core: true },
  { key: "leave", name: "Leave", nameBn: "ছুটি", core: true },
  { key: "payroll", name: "Payroll", nameBn: "বেতন", core: true },
  { key: "adms", name: "ZKTeco biometric devices", nameBn: "বায়োমেট্রিক ডিভাইস", defaultOn: false, requires: ["attendance"] },
  { key: "ot", name: "Overtime", nameBn: "ওভারটাইম", defaultOn: true, requires: ["attendance", "payroll"] },
  { key: "ait", name: "Income tax (AIT) calculation", nameBn: "আয়কর হিসাব", defaultOn: true, requires: ["payroll"] },
  { key: "pf", name: "Provident fund", nameBn: "প্রভিডেন্ট ফান্ড", defaultOn: false, requires: ["payroll"] },
  { key: "loan", name: "Loans & advances", nameBn: "ঋণ ও অগ্রিম", defaultOn: false, requires: ["payroll"] },
  { key: "multi_branch", name: "Multiple branches", nameBn: "একাধিক শাখা", defaultOn: false },
  { key: "recruitment", name: "Recruitment & career page", nameBn: "নিয়োগ", defaultOn: false },
  { key: "performance", name: "Performance reviews", nameBn: "কর্মমূল্যায়ন", defaultOn: false },
  { key: "lifecycle", name: "Employee lifecycle", nameBn: "কর্মজীবন চক্র", defaultOn: false },
  { key: "training", name: "Training", nameBn: "প্রশিক্ষণ", defaultOn: false },
  { key: "assets", name: "Asset management", nameBn: "সম্পদ ব্যবস্থাপনা", defaultOn: false },
  { key: "field", name: "Mobile attendance & field force (route map)", nameBn: "মোবাইল হাজিরা ও ফিল্ড ফোর্স (রুট ম্যাপ)", defaultOn: false, requires: ["attendance"] },
  { key: "communication", name: "Announcements & notice board", nameBn: "ঘোষণা ও নোটিশ", defaultOn: true },
  { key: "sms", name: "SMS notifications", nameBn: "এসএমএস", defaultOn: false },
  { key: "whatsapp", name: "WhatsApp sharing", nameBn: "হোয়াটসঅ্যাপ", defaultOn: true },
  { key: "email", name: "Email notifications", nameBn: "ইমেইল", defaultOn: true },
];
export const MODULE_KEYS = new Set(MODULES.map((m) => m.key));

export function effectiveModules(planFeatures: Record<string, unknown> | null | undefined,
                                 overrides: { moduleKey: string; enabled: boolean }[] = []): Record<string, boolean> {
  const ov = new Map(overrides.map((o) => [o.moduleKey, o.enabled]));
  const out: Record<string, boolean> = {};
  for (const m of MODULES) {
    if (m.core) { out[m.key] = true; continue; }
    const plan = planFeatures?.[m.key];
    out[m.key] = ov.has(m.key) ? !!ov.get(m.key) : typeof plan === "boolean" ? plan : !!m.defaultOn;
  }
  // A module whose dependency is off is off too (e.g. OT without payroll can't run).
  for (const m of MODULES) if (out[m.key] && m.requires?.some((r) => !out[r])) out[m.key] = false;
  return out;
}

/** Validate a Super Admin change before saving. Returns an error message or null. */
export function validateModuleChange(key: string, enabled: boolean, current: Record<string, boolean>): string | null {
  const m = MODULES.find((x) => x.key === key);
  if (!m) return `Unknown module "${key}"`;
  if (m.core && !enabled) return `${m.name} is a core module and can't be switched off`;
  if (enabled) {
    const missing = (m.requires || []).filter((r) => !current[r]);
    if (missing.length) return `${m.name} needs ${missing.join(", ")} switched on first`;
  } else {
    const dependents = MODULES.filter((x) => current[x.key] && x.requires?.includes(key)).map((x) => x.name);
    if (dependents.length) return `Switch off ${dependents.join(", ")} first; they depend on ${m.name}`;
  }
  return null;
}
